import React, { useEffect, useMemo, type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext, useAccountWriteContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Body1, Heading3 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { Input } from '@ui/components/Input'
import { Link } from '@ui/components/Link'

import { isTickmillPartner } from '@utils/companyName.utils'
import { CommonPaths } from '@utils/path'

import { IBTestSubCategory } from '@model/TestSectionsDto'

import { useIBReferralCodes } from '@hooks/useIBReferralCodes'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'
import { HowIBWorksModal } from '@shared/modals/HowIBWorks.modal'

import styles from './IBSignupThankYouPage.module.scss'

export const IBSignupThankYouPage = (): JSX.Element => {
  const { subCategoryId } = useParams<{ subCategoryId: IBTestSubCategory }>()
  const navigate = useNavigate()

  const { ibCode, mtCode, isLoading } = useIBReferralCodes()

  const { refreshAccount } = useAccountWriteContext()
  const { account } = useAccountReadContext()
  const locale = useSessionLanguage()
  const isPartner = isTickmillPartner(account)
  const howIBWorksModal = useModal(HowIBWorksModal)

  const navigateTo = () => navigate(CommonPaths.IntroducingBrokerWallets)

  const { t } = useTranslation()

  /**
   * TODO: refresh account on mount to get updated IB status and referral codes.
   * TODO important: this must happen AFTER navigation to avoid race condition with route guards.
   * TODO: refreshAccount is called before navigation (in the questionnaire page), the account status updates asynchronously, causing routing guards to redirect to /dashboard instead of allowing access to this thank-you page.
   * TODO: locale changes are already handled by global SessionSettingsContext, so we don't need it here, we want only call it once
   */
  useEffect(() => {
    refreshAccount(locale)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isMultiTier = subCategoryId === IBTestSubCategory.MultiTier

  const code = useMemo(() => {
    if (isMultiTier && mtCode) {
      return mtCode
    }
    if (ibCode) {
      return ibCode
    }
    return ''
  }, [mtCode, ibCode, isMultiTier])

  return (
    <div className={styles.ibSignupThankYouPage}>
      <div className={styles.wrapper}>
        <div className={styles.loadingWrapper}>
          <div className={styles.feedbackWrapper}>
            <div className={styles.header}>
              <Heading3>{t('Thank You!')}</Heading3>
              <Body1>
                {isMultiTier && mtCode
                  ? t('IB.You have become an Introducing Broker in Multi-tier scheme')
                  : t('IB.You have become an Introducing Broker!')}
              </Body1>
              {!isPartner && (
                <Body1>
                  {t(
                    'IB.Invite traders to join Tickmill by using your personal link and get a permanent commission on every trade they make.'
                  )}
                </Body1>
              )}
            </div>
            <Loading isLoading={isLoading && !code} showLoadingIcon>
              {!isPartner && (
                <Input
                  type='text'
                  canCopy
                  fullWidth
                  readOnly
                  label={`${t('IB.Your Referral Code')}:`}
                  value={code}
                />
              )}
            </Loading>
            {ibCode && !isMultiTier && !isPartner && (
              <div>
                <Link size='lg' onClick={() => howIBWorksModal.open({ ibCode, isMt: false })}>
                  {t('IB.How IB works?')}
                </Link>
              </div>
            )}
            {mtCode && isMultiTier && !isPartner && (
              <div>
                <Link
                  size='lg'
                  onClick={() =>
                    howIBWorksModal.open({
                      isMt: true,
                      mtCode,
                    })
                  }
                >
                  {t('IB.MultiTier.How Multi-tier works')}?
                </Link>
              </div>
            )}
            <Box fullWidth className={styles.submitButton}>
              <Button.Secondary size='lg' fullWidth onClick={navigateTo} loading={isLoading}>
                {t('Ok')}
              </Button.Secondary>
            </Box>
          </div>
        </div>
      </div>
    </div>
  )
}
